/* eslint-disable @typescript-eslint/no-explicit-any */
import { PublicClientApplication } from "@azure/msal-browser";

export const BASE_URL =
  process.env.NODE_ENV === "development"
    ? "http://127.0.0.1:8000" // Local environment
    : process.env.REACT_APP_SERVICE_URL; // Dev or production environment

const attemptTokenRefresh = async (instance: PublicClientApplication): Promise<string | null> => {
  try {
    const account = instance.getActiveAccount();
    if (!account) return null;

    const tokenResponse = await instance.acquireTokenSilent({
      // scopes: ["User.Read"],
      scopes: [`${process.env.REACT_APP_CLIENT_ID}/.default` , "offline_access"],
      account: account,
    });

    localStorage.setItem("tokenKey", tokenResponse.accessToken);
    if (tokenResponse.expiresOn) {
      localStorage.setItem(
        "tokenExpiresOn",
        String(Math.floor(tokenResponse.expiresOn.getTime() / 1000))
      );
    }

    return tokenResponse.accessToken;
  } catch (error) {
    console.error("Token refresh failed:", error);
    return null;
  }
};


export const apiService = async (
  endpoint: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  body?: Record<any, any> | FormData | string,
  headers: Record<string, string> = { "Content-Type": "application/json" },
  msalInstance?: PublicClientApplication
): Promise<any> => {
  try {
    let url = `${BASE_URL}${endpoint}`;
    const tokenKey = localStorage.getItem("tokenKey");
    const roleKey = localStorage.getItem("roleKey");

    // Prepare headers with auth tokens
    const authHeaders: Record<string, string> = {
      Authorization: tokenKey ? `Bearer ${tokenKey}` : "",
      "X-Role-Key": roleKey || "",
      ...headers,
    };

    const isFormData = body instanceof FormData;
    const finalHeaders = isFormData
      ? {
          Authorization: authHeaders.Authorization,
          "X-Role-Key": authHeaders["X-Role-Key"],
        }
      : authHeaders;

    // Handle GET method with query parameters
    if (method === "GET" && body && typeof body === "object" && !isFormData) {
      const queryParams = new URLSearchParams(body as Record<string, string>);
      url += `?${queryParams.toString()}`;
    }

    // Initial request
    const response = await fetch(url, {
      method,
      headers: isFormData
        ? finalHeaders
        : { ...finalHeaders, "Content-Type": "application/json" },
      body: method !== "GET" && body 
        ? (isFormData ? body : JSON.stringify(body)) 
        : undefined,
    });

    // Handle 401 Unauthorized - Attempt token refresh if MSAL instance is provided
    if (response.status === 401 && msalInstance) {
      const newToken = await attemptTokenRefresh(msalInstance);
      if (newToken) {
        // Retry the original request with new token
        const retryResponse = await fetch(url, {
          method,
          headers: {
            ...finalHeaders,
            Authorization: `Bearer ${newToken}`,
            ...(isFormData ? {} : { "Content-Type": "application/json" }),
          },
          body: method !== "GET" && body 
            ? (isFormData ? body : JSON.stringify(body)) 
            : undefined,
        });

        if (!retryResponse.ok) {
          throw new Error(`Retry failed with status: ${retryResponse.status}`);
        }

        const contentType = retryResponse.headers.get("Content-Type");
        return contentType && contentType.includes("application/json")
          ? await retryResponse.json()
          : await retryResponse.text();
      }
      // If refresh fails, clear token and redirect
      localStorage.removeItem("tokenKey");
      window.location.href = "/login";
      return;
    }

    // Handle non-2xx responses
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;
      
      try {
        const errorResponse = JSON.parse(errorText);
        errorMessage += `, message: ${errorResponse.message || response.statusText}`;
      } catch {
        errorMessage += `, message: ${response.statusText}`;
      }

      throw new Error(errorMessage);
    }

    // Return parsed response
    const contentType = response.headers.get("Content-Type");
    return contentType && contentType.includes("application/json")
      ? await response.json()
      : await response.text();
  } catch (error) {
    console.error("Error in API call:", error);
    throw error;
  }
};

/**
 * Utility function to check if token is valid
 * @param expiresOn Token expiration timestamp
 * @returns boolean indicating if token is still valid
 */
export const isTokenValid = (expiresOn: number | undefined): boolean => {
  if (!expiresOn) return false;
  const currentTime = Math.floor(Date.now() / 1000);
  return expiresOn > currentTime;
};